import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { IconButton } from "react-native-paper";
import { useTheme } from "@/src/styles/theme-provider";

import { usePostDetail } from "@/src/hooks/features/posts/use-post-detail";
import { CloseCircle, Send } from "iconsax-react-nativejs";
import ShareModal from "../action-modals/share-modal";
import PostDetailSkeleton from "./loading-skeletons/post-detail-skeleton";
import { FileAttachment } from "./post/file-attachment";
import { MediaCarousel } from "./post/media-carousel";
import PostActions from "./post/post-actions";
import CommentsList from "./post/post-comments";
import PostHeader from "./post/post-header";

dayjs.extend(relativeTime);

type Props = Readonly<{
  visible: boolean;
  postId: string;
  initialIndex?: number;
  onClose: () => void;
}>;

const PostDetailModal = ({
  visible,
  postId,
  initialIndex = 0,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  const {
    user,
    activePost,
    isPostLoading,
    mediaUrls,
    fileUrls,
    userReaction,
    handleReact,
    comments,
    isCommentsLoading,
    isCommentsError,
    refetchComments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    commentText,
    setCommentText,
    isCommentDisabled,
    isCommentPending,
    handlePostComment,
    inputRef,
    replyTarget,
    handleReply,
    handleCancelReply,
    shareModalVisible,
    setShareModalVisible,
    handleShareNotification,
    isSaved,
    handleToggleSave,
  } = usePostDetail({ postId, visible });

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View
        style={varStyle}
        className="flex-1 bg-black/40 justify-center items-center"
      >
        <KeyboardAvoidingView
          style={{
            flex: 1,
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
        >
          <View
            style={{ width: "90%", maxWidth: 700, maxHeight: "90%" }}
            className="bg-chalk rounded-2xl overflow-hidden"
          >
            {isPostLoading || !activePost ? (
              <>
                <TouchableOpacity
                  onPress={onClose}
                  hitSlop={8}
                  className="absolute top-3 right-3 hover:bg-ash-3 hover:rounded-full"
                >
                  <CloseCircle size={24} color={themeVars["--color-slate-default"] as string} />
                </TouchableOpacity>
                <PostDetailSkeleton />
              </>
            ) : (
              <>
                <PostHeader post={activePost} showClose onClose={onClose} />
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={{ paddingBottom: 80 }}
                >
                  <View className="px-4 pb-1 mt-2">
                    <Text className="text-sm leading-5 text-onyx">
                      {activePost.content}
                    </Text>
                  </View>

                  {(fileUrls ?? []).length > 0 && (
                    <View className="px-4 my-2">
                      <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{ gap: 8 }}
                      >
                        {(fileUrls ?? []).map((url) => (
                          <FileAttachment key={url} fileUrl={url} />
                        ))}
                      </ScrollView>
                    </View>
                  )}

                  {(mediaUrls ?? []).length > 0 && (
                    <MediaCarousel
                      media={mediaUrls ?? []}
                      initialIndex={initialIndex}
                    />
                  )}

                  <PostActions
                    userReaction={userReaction?.type}
                    reactionCounts={activePost.reactionCount}
                    reactionTotal={Object.values(
                      activePost.reactionCount || {},
                    ).reduce((sum, val) => (sum ?? 0) + (val ?? 0), 0)}
                    onReact={handleReact}
                    commentCount={activePost.commentCount}
                    onShare={() => setShareModalVisible(true)}
                  />

                  <CommentsList
                    comments={comments}
                    isLoading={isCommentsLoading}
                    isError={isCommentsError}
                    onRefresh={refetchComments}
                    onReply={handleReply}
                    onLoadMore={fetchNextPage}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                  />
                </ScrollView>

                <View className="absolute bottom-0 left-0 right-0 bg-chalk border-t border-ash-3 pl-4 py-2 flex-row items-center">
                  <Image
                    source={{ uri: user?.profileImageUrl ?? "" }}
                    className="w-10 h-10 rounded-full mr-3"
                  />
                  {replyTarget && (
                    <TouchableOpacity
                      onPress={handleCancelReply}
                      hitSlop={8}
                      className="hover:bg-ash-3 hover:rounded-full"
                    >
                      <CloseCircle size={16} className="mr-2" />
                    </TouchableOpacity>
                  )}
                  <TextInput
                    ref={inputRef}
                    value={commentText}
                    onChangeText={setCommentText}
                    placeholder={
                      replyTarget
                        ? `${t("LABEL_REPLYING_TO")} ${replyTarget.author}...`
                        : t("LABEL_LEAVE_A_COMMENT")
                    }
                    style={{ color: themeVars["--color-onyx-default"] as string }}
                    placeholderTextColor={themeVars["--color-ash-1"] as string}
                    className="flex-1 border-ash-3 rounded-full px-4 py-2 text-sm bg-ash focus:outline focus:outline-primary"
                    onKeyPress={({ nativeEvent }) => {
                      if (
                        nativeEvent.key === "Enter" &&
                        !(nativeEvent as any).shiftKey &&
                        !isCommentDisabled &&
                        !isCommentPending
                      ) {
                        handlePostComment();
                      }
                    }}
                  />
                  <IconButton
                    loading={isCommentPending}
                    icon={() => (
                      <Send
                        color={
                          isCommentDisabled ? themeVars["--color-ash-2"] as string : themeVars["--color-primary-default"] as string
                        }
                        size={26}
                        variant={isCommentDisabled ? "Outline" : "Bold"}
                      />
                    )}
                    disabled={isCommentDisabled || isCommentPending}
                    iconColor={themeVars["--color-primary-default"] as string}
                    onPress={handlePostComment}
                  />
                </View>
              </>
            )}
          </View>
        </KeyboardAvoidingView>
      </View>

      <ShareModal
        visible={shareModalVisible}
        onClose={() => setShareModalVisible(false)}
        resourceId={activePost?.ItemId ?? ""}
        resourcePath="feeds"
        onShare={handleShareNotification}
      />
    </Modal>
  );
};

export default PostDetailModal;
